import axios from './axiosInstance';
import axiosLib from 'axios';
import { SpatialFeature } from '../../models/SpatialFeature';
import {
  APISpatialFeatureClass,
  APISpatialFeature,
  APISpatialFeatureClassProperty,
  APISpatialFeatureProperty,
} from '../../types';

interface SportAreaGisParams {
  sport: string;
  areaId: string;
  // Optional: when omitted, the module-less endpoint is used, which returns
  // every spatial feature / class the user can access across all GIS modules.
  gisModule?: string;
  abortSignal?: AbortSignal;
}

interface GetSpatialFeatureParams {
  sport: string;
  areaId: string;
  gisModule?: string;
  spatialFeatureId: string;
  abortSignal?: AbortSignal;
}

interface GetHistoricalSpatialFeaturesParams extends SportAreaGisParams {
  timestamp: number;
}

// The writable body for a spatial FEATURE, matching the server's
// validateGISSpatialFeature contract (UllrServerless shared/sharedGISLogic.ts —
// the server's own SpatialFeature type still has `geometry: any`, so this is
// the enforced version of that contract). ⚠ `geometry` is typed `string` on
// purpose: the server JSON.parses it and 400s on a bare object ("Invalid
// geometry. Invalid json."), so the stringified-GeoJSON requirement is held by
// the compiler rather than comments and convention. GETs return the same
// stringified shape (ST_AsGeoJSON).
export interface SpatialFeatureCreateBody {
  class_id: string;
  /** JSON-stringified GeoJSON geometry object (not a Feature/FeatureCollection). */
  geometry: string;
  sports: string[];
  /**
   * Omit to let the server derive publicity from the CLASS's audience — a private
   * item type (lift towers/stations) then mints private features. Send an explicit
   * boolean only when the user actually chose one; a hardcoded `true` here is what
   * made every newly drawn or imported tower publicly visible until it was flipped
   * by hand.
   */
  is_public?: boolean;
  properties?: APISpatialFeatureProperty[] | null;
  parent_id?: string | null;
}

// Updates are NOT sparse — callers send the full feature (the model's
// toObject() plus edits, see asset-edit/model.ts), so `id` is required and
// the server-populated read fields ride along (the server ignores them).
export interface SpatialFeatureUpdateBody extends SpatialFeatureCreateBody {
  id: string;
  timestamp?: number | null;
  created_at?: number;
  created_by?: string;
  updated_at?: number;
  updated_by?: string;
  archived_at?: number;
  archived_by?: string;
  /** toObject() carries the model's camelCase areaId — a harmless extra key. */
  areaId?: string;
}

/**
 * A feature with NO geometry: a sub-component whose class is `optional` or
 * `never` located (lift components — the parent already answers "where").
 * `geometry` is left out of the request altogether; the server rejects any
 * geometry, even the string "null", for a never-located class.
 */
export type UnlocatedSpatialFeatureCreateBody = Omit<SpatialFeatureCreateBody, 'geometry'> & {
  geometry?: null;
};

interface CreateSpatialFeatureParams {
  sport: string;
  areaId: string;
  // Always MODULE-LESS on create: the handler derives the real module from the
  // feature's class and enforces that module's subscription, so a non-lift
  // (generic GIS) class doesn't 403 on the lift-gated route (same reasoning as
  // the P4 write fix).
  spatialFeature: SpatialFeatureCreateBody | UnlocatedSpatialFeatureCreateBody;
  /** Optional idempotency key — the PUT create route runs the backend's
   *  IdempotentRequests middleware, keyed on the x-request-id header. Only a
   *  2xx is saved, so keep ONE id per logical create across retries. */
  requestId?: string;
}

interface UpdateSpatialFeatureParams {
  sport: string;
  areaId: string;
  // Optional: when omitted, the module-less write endpoint is used. The write
  // handler derives the real module from the feature's class and enforces that
  // module's subscription, so module-less is strictly more correct — a non-lift
  // (generic GIS) feature no longer 403s on the lift-gated `/gis/lifts/...` route.
  gisModule?: string;
  spatialFeature: SpatialFeatureUpdateBody;
}

interface ArchiveSpatialFeatureParams {
  sport: string;
  areaId: string;
  gisModule?: string;
  spatialFeatureId: string;
}

interface UpdateSpatialFeaturesParams {
  sport: string;
  areaId: string;
  gisModule?: string;
  spatialFeatures: SpatialFeatureUpdateBody[];
}

// The writable body for a spatial-feature CLASS (custom item type). Mirrors the
// UllrCLI `spatial-feature-classes create` payload exactly (see
// UllrCLI/src/commands/spatial-feature-classes.ts). Icon is a bare kebab MDI
// name (no `mdi-` prefix); color is a CSS color string.
interface SpatialFeatureClassCreateBody {
  slug: string;
  name: string;
  audience: 'public' | 'private';
  geometry_type?: 'point' | 'line' | 'polygon';
  is_abstract?: boolean;
  icon?: string;
  color?: string;
  parent_id?: string;
  sports?: string[];
  properties?: APISpatialFeatureClassProperty[];
}

// A SPARSE update body: only changed keys. `slug` / `parent_id` / `hierarchy`
// are immutable and must never be sent. When `properties` is present it is the
// COMPLETE replacement array (the update handler replaces the whole array).
type SpatialFeatureClassUpdateBody = Partial<
  Omit<SpatialFeatureClassCreateBody, 'slug' | 'parent_id'>
>;

interface CreateSpatialFeatureClassParams {
  sport: string;
  areaId: string;
  spatialFeatureClass: SpatialFeatureClassCreateBody;
  /** `x-request-id`: only a 2xx is saved, so keep ONE per logical create across retries. */
  requestId?: string;
}

interface UpdateSpatialFeatureClassParams {
  sport: string;
  areaId: string;
  classId: string;
  changes: SpatialFeatureClassUpdateBody;
}

interface ArchiveSpatialFeatureClassParams {
  sport: string;
  areaId: string;
  classId: string;
}

interface SportAreaAbortParams {
  sport: string;
  areaId: string;
  abortSignal?: AbortSignal;
}

interface GetHistoricalLiftsParams extends SportAreaAbortParams {
  timestamp: number;
}

interface GetAllSpatialFeaturesParams extends SportAreaAbortParams {
  /** Dead param — the backend enforces per-module access itself. Optional so
   *  new callers don't have to pass it. */
  ullrPermissions?: any;
}

interface GetAllHistoricalSpatialFeaturesParams extends SportAreaAbortParams {
  timestamp: number;
  ullrPermissions: any;
}

interface GetAllSpatialFeatureClassesParams extends SportAreaAbortParams {
  ullrPermissions: any;
}


const getSpatialFeatureClasses = ({ sport, areaId, gisModule, abortSignal }: SportAreaGisParams): Promise<APISpatialFeatureClass[]> => {
  const CancelToken = axiosLib.CancelToken;
  const source = CancelToken.source();
  if (abortSignal) {
    abortSignal.addEventListener('abort', () => {
      source.cancel('Operation canceled by the user.');
    });
  }

  const url = gisModule
    ? `/${sport}/areas/${areaId}/gis/${gisModule}/spatialfeatureclasses`
    : `/${sport}/areas/${areaId}/gis/spatialfeatureclasses`;
  return axios.get(url, { cancelToken: source.token })
    .then((response) => {
      const classes = response?.data || [];
      return classes;
    })
    .catch((error) => {
      console.log(error);
      throw error;
    });
};

const getSpatialFeatures = ({ sport, areaId, gisModule, abortSignal }: SportAreaGisParams): Promise<any[]> => {
  const CancelToken = axiosLib.CancelToken;
  const source = CancelToken.source();
  if (abortSignal) {
    abortSignal.addEventListener('abort', () => {
      source.cancel('Operation canceled by the user.');
    });
  }

  const url = gisModule
    ? `/${sport}/areas/${areaId}/gis/${gisModule}/spatialfeatures`
    : `/${sport}/areas/${areaId}/gis/spatialfeatures`;
  return axios.get(url, { cancelToken: source.token })
    .then((response) => {
      const features = response?.data || [];
      return features.map((feature: any) => new SpatialFeature(feature));
    })
    .catch((error) => {
      console.log(error);
      throw error;
    });
};

const getHistoricalSpatialFeatures = ({ sport, areaId, gisModule, timestamp, abortSignal }: GetHistoricalSpatialFeaturesParams): Promise<any[]> => {
  const CancelToken = axiosLib.CancelToken;
  const source = CancelToken.source();
  if (abortSignal) {
    abortSignal.addEventListener('abort', () => {
      source.cancel('Operation canceled by the user.');
    });
  }

  const url = gisModule
    ? `/${sport}/areas/${areaId}/gis/${gisModule}/spatialfeatures/history?timestamp=${timestamp}`
    : `/${sport}/areas/${areaId}/gis/spatialfeatures/history?timestamp=${timestamp}`;
  return axios.get(url, { cancelToken: source.token })
    .then((response) => {
      const features = response.data;
      return features.map((feature: any) => new SpatialFeature(feature));
    })
    .catch((error) => {
      console.log(error);
      throw error;
    });
};

const getSpatialFeature = ({ sport, areaId, gisModule, spatialFeatureId, abortSignal }: GetSpatialFeatureParams): Promise<any> => {
  const CancelToken = axiosLib.CancelToken;
  const source = CancelToken.source();
  if (abortSignal) {
    abortSignal.addEventListener('abort', () => {
      source.cancel('Operation canceled by the user.');
    });
  }

  let url;
  if (gisModule) {
    url = `/${sport}/areas/${areaId}/gis/${gisModule}/spatialfeatures/${spatialFeatureId}`;
  } else {
    url = `/${sport}/areas/${areaId}/gis/spatialfeatures/${spatialFeatureId}`;
  }
  return axios.get(url, { cancelToken: source.token })
    .then((response) => {
      const feature = response.data;
      return new SpatialFeature(feature);
    })
    .catch((error) => {
      console.log(error);
      throw error;
    });
};

// PUT — create a spatial feature (ullr-139 Phase 6). MODULE-LESS route only (see
// CreateSpatialFeatureParams). The body type holds geometry to a string, but
// untyped JS callers can still slip an object through — normalize as a runtime
// backstop (see SpatialFeatureCreateBody for the server contract). Returns the
// new `SpatialFeature` model.
/**
 * A module's features as the API returns them — class, hierarchy and
 * `properties` untouched (`getSpatialFeatures` wraps each in the model).
 * For code that reads the raw shape, such as the onboarding pack's lift list.
 */
const getRawSpatialFeatures = ({ sport, areaId, gisModule }: SportAreaGisParams): Promise<Record<string, unknown>[]> => {
  const url = gisModule
    ? `/${sport}/areas/${areaId}/gis/${gisModule}/spatialfeatures`
    : `/${sport}/areas/${areaId}/gis/spatialfeatures`;
  return axios.get(url).then((response) => (Array.isArray(response?.data) ? response.data : []));
};

// The module-less feature list answered as it came: NO `[]` for a missing
// response (the 401 interceptor resolves `undefined`, a cancelled request
// `{}`). For the SWR resource (`src/data/spatialFeatures.ts`), which must not
// cache a failed read as a resort with no features.
const getRawSpatialFeaturesStrict = ({ sport, areaId }: { sport: string; areaId: string }): Promise<Record<string, unknown>[] | undefined> => {
  return axios.get(`/${sport}/areas/${areaId}/gis/spatialfeatures`)
    .then((response) => response?.data);
};

const createSpatialFeature = ({ sport, areaId, spatialFeature, requestId }: CreateSpatialFeatureParams): Promise<any> => {
  const url = `/${sport}/areas/${areaId}/gis/spatialfeatures`;
  const { geometry, ...rest } = spatialFeature;
  const body = geometry === null || geometry === undefined
    ? rest
    : { ...rest, geometry: typeof geometry === 'string' ? geometry : JSON.stringify(geometry) };
  // No config argument at all without a requestId, so existing callers send
  // exactly the request they always did.
  const request = requestId
    ? axios.put(url, body, { headers: { 'x-request-id': requestId } })
    : axios.put(url, body);
  return request
    .then((response) => {
      return new SpatialFeature(response.data);
    })
    .catch((error) => {
      console.log(error);
      throw error;
    });
};

const updateSpatialFeature = ({ sport, areaId, gisModule, spatialFeature }: UpdateSpatialFeatureParams): Promise<any> => {
  const url = gisModule
    ? `/${sport}/areas/${areaId}/gis/${gisModule}/spatialfeatures/${spatialFeature.id}`
    : `/${sport}/areas/${areaId}/gis/spatialfeatures/${spatialFeature.id}`;
  return axios.post(url, {
    ...spatialFeature,
   })
    .then((response) => {
      const updatedSpatialFeature = response.data;
      return new SpatialFeature(updatedSpatialFeature);
    })
    .catch((error) => {
      console.log(error);
      throw error;
    });
};

const archiveSpatialFeature = ({ sport, areaId, gisModule, spatialFeatureId }: ArchiveSpatialFeatureParams): Promise<void> => {
  const url = gisModule
    ? `/${sport}/areas/${areaId}/gis/${gisModule}/spatialfeatures/${spatialFeatureId}/archive`
    : `/${sport}/areas/${areaId}/gis/spatialfeatures/${spatialFeatureId}/archive`;
  return axios.post(url, {})
    .then(() => {})
    .catch((error) => {
      console.log(error);
      throw error;
    });
};

const updateSpatialFeatures = ({ sport, areaId, gisModule, spatialFeatures }: UpdateSpatialFeaturesParams): Promise<any[]> => {
  const url = gisModule
    ? `/${sport}/areas/${areaId}/gis/${gisModule}/spatialfeatures`
    : `/${sport}/areas/${areaId}/gis/spatialfeatures`;
  return axios.post(url, spatialFeatures)
    .then((response) => {
      const updatedSpatialFeatures = response.data;
      return (updatedSpatialFeatures || []).map((updatedSpatialFeature: any) => new SpatialFeature(updatedSpatialFeature));
    })
    .catch((error) => {
      console.log(error);
      throw error;
    });
};

// ============================ SPATIAL FEATURE CLASSES (custom item types) ============================
// Module-less writes only (ullr-138 Phase 2). Custom item types live under the
// generic GIS module; the handler derives the real module from the class, so a
// non-lift class doesn't 403 on a lift-gated route (same reasoning as the
// spatial-feature write path). Bodies mirror the UllrCLI payloads verbatim.

// PUT — create a custom spatial-feature class. Body is the passed object
// ({ slug, name, audience, geometry_type?, is_abstract?, icon?, color?,
// parent_id?, sports?, properties? }). Returns the created class.
const createSpatialFeatureClass = ({ sport, areaId, spatialFeatureClass, requestId }: CreateSpatialFeatureClassParams): Promise<APISpatialFeatureClass> => {
  const url = `/${sport}/areas/${areaId}/gis/spatialfeatureclasses`;
  // No config argument at all without a requestId, so existing callers send
  // exactly the request they always did.
  const request = requestId
    ? axios.put(url, { ...spatialFeatureClass }, { headers: { 'x-request-id': requestId } })
    : axios.put(url, { ...spatialFeatureClass });
  return request
    .then((response) => {
      return response.data as APISpatialFeatureClass;
    })
    .catch((error) => {
      console.log(error);
      throw error;
    });
};

// POST — update a custom class. `changes` is a SPARSE body (only changed keys);
// the handler applies each field only when present. `slug` / `parent_id` /
// `hierarchy` are immutable and must not be sent. When `properties` is present
// it REPLACES the whole array. Returns the updated class.
const updateSpatialFeatureClass = ({ sport, areaId, classId, changes }: UpdateSpatialFeatureClassParams): Promise<APISpatialFeatureClass> => {
  const url = `/${sport}/areas/${areaId}/gis/spatialfeatureclasses/${classId}`;
  return axios.post(url, {
    ...changes,
  })
    .then((response) => {
      return response.data as APISpatialFeatureClass;
    })
    .catch((error) => {
      console.log(error);
      throw error;
    });
};

// POST — archive (soft delete) a custom class. Empty body. Returns the archived
// class.
const archiveSpatialFeatureClass = ({ sport, areaId, classId }: ArchiveSpatialFeatureClassParams): Promise<APISpatialFeatureClass> => {
  const url = `/${sport}/areas/${areaId}/gis/spatialfeatureclasses/${classId}/archive`;
  return axios.post(url, {})
    .then((response) => {
      return response.data as APISpatialFeatureClass;
    })
    .catch((error) => {
      console.log(error);
      throw error;
    });
};

const getLifts = ({ sport, areaId, abortSignal }: SportAreaAbortParams): Promise<any[]> => {
  return getSpatialFeatures({ sport, areaId, gisModule: 'lifts', abortSignal });
};

const getHistoricalLifts = ({ sport, areaId, timestamp, abortSignal }: GetHistoricalLiftsParams): Promise<any[]> => {
  return getHistoricalSpatialFeatures({ sport, areaId, gisModule: 'lifts', timestamp, abortSignal });
};

const getLift = ({ sport, areaId, spatialFeatureId, abortSignal }: GetSpatialFeatureParams): Promise<any> => {
  return getSpatialFeature({ sport, areaId, gisModule: 'lifts', spatialFeatureId, abortSignal });
};

/**
 * Fetches every spatial feature the current user can access across all GIS
 * modules in a single request via the module-less endpoint (mirrors UllrMobile).
 * The backend enforces per-module access, so no client-side module gating is
 * required. `ullrPermissions` is retained on the params for call-site
 * compatibility but is no longer used to gate the request.
 */
const getAllSpatialFeatures = ({ sport, areaId, abortSignal }: GetAllSpatialFeaturesParams): Promise<any[]> => {
  return getSpatialFeatures({ sport, areaId, abortSignal });
}

const getAllHistoricalSpatialFeatures = ({ sport, areaId, timestamp, abortSignal }: GetAllHistoricalSpatialFeaturesParams): Promise<any[]> => {
  return getHistoricalSpatialFeatures({ sport, areaId, timestamp, abortSignal });
}

const getAllSpatialFeatureClasses = ({ sport, areaId, abortSignal }: GetAllSpatialFeatureClassesParams): Promise<APISpatialFeatureClass[]> => {
  return getSpatialFeatureClasses({ sport, areaId, abortSignal });
}

// The module-less class list, answered as it came: NO `[]` for a missing
// response. The axios instance's 401 interceptor resolves with `undefined`, and
// a caller that plans creates from this list (the install wizard) must see
// that as a failed read, not as a resort with no classes.
const getSpatialFeatureClassesStrict = ({ sport, areaId }: { sport: string; areaId: string }): Promise<APISpatialFeatureClass[] | undefined> => {
  return axios.get(`/${sport}/areas/${areaId}/gis/spatialfeatureclasses`)
    .then((response) => response?.data);
};




const gisFunctions = {
  // create
  createSpatialFeature,
  createSpatialFeatureClass,
  // retrieve
  getAllSpatialFeatureClasses,
  getSpatialFeatureClassesStrict,
  getSpatialFeatures,
  getRawSpatialFeatures,
  getRawSpatialFeaturesStrict,
  getSpatialFeature,
  getLifts,
  getLift,
  getAllSpatialFeatures,
  getAllHistoricalSpatialFeatures,
  // update
  updateSpatialFeature,
  updateSpatialFeatures,
  updateSpatialFeatureClass,
  // delete
  archiveSpatialFeature,
  archiveSpatialFeatureClass,
};

export default gisFunctions;
